import React, { useState, useEffect, useContext, useRef } from '@wordpress/element';
import groupBy from 'lodash.groupby';
import CartProduct from './CartProduct';
import { didSurfsideLoad, trackSurfsideProductsLoop } from '../Surfside/util';
import { gtmTrackEvent, processCartSpecials } from '../../analytics';
import { RetailerIdContext } from '../../App';
import { useCartData, useSpecialsList } from '../../hooks/dutchie-queries';
import usePricingType from '../../hooks/usePricingType';
import useFetchRest from '../../hooks/useFetchRest';

const CartProducts = ({ surfside, analytics, cartPage, cartIsOpen, cartSidebar }) => {
    const retailerId = useContext(RetailerIdContext);
    const { data: cartData, isFetched: isFetchedCartData } = useCartData(retailerId);
    const { data: specialsList, isFetched: isFetchedSpecials } = useSpecialsList(retailerId);
    const { data: menuData, isFetched: isFetchedMenuData } = useFetchRest(`data/menu?retailerId=${retailerId}`);
    const [pricingType] = usePricingType(retailerId);
        
    const [cartSpecials, setCartSpecials] = useState({});
    const hasTrackedViewCart = useRef(false);
    const previousCartIsOpen = useRef(cartIsOpen);
    
    const [groupedItems, setGroupedItems] = useState(null);

    useEffect(() => {
        if (isFetchedCartData && isFetchedSpecials && isFetchedMenuData && cartData?.items) {
            const specials = processCartSpecials(cartData.items, specialsList, menuData);
            setCartSpecials(specials);
        }
    }, [isFetchedCartData, isFetchedSpecials, isFetchedMenuData, cartData, specialsList, menuData]);

    useEffect(() => {
        if (isFetchedCartData && cartData && Array.isArray(cartData.items)) {
            setGroupedItems(groupBy(cartData.items, (item) => item.productId));
        }
    }, [cartData, isFetchedCartData]);

    useEffect(() => {
        if (
            isFetchedCartData &&
            cartData &&
            Array.isArray(cartData.items) &&
            cartData.items.length > 0 &&
            surfside &&
            surfside.enable &&
            cartPage === 'cart'
        ) {
            didSurfsideLoad(() => {
                trackSurfsideProductsLoop(cartData.items, 'cart');
            });
        }
    }, [cartData, isFetchedCartData, cartPage, surfside]);

    useEffect(() => {
        const shouldTrackViewCart = 
            !previousCartIsOpen.current &&
            cartIsOpen &&
            cartSidebar &&
            !hasTrackedViewCart.current &&
            isFetchedCartData &&
            cartData &&
            Array.isArray(cartData.items) &&
            cartData.items.length > 0 && 
            analytics && 
            analytics.gtm_id;

        if (shouldTrackViewCart) {
            const cartItemsWithSpecials = cartData.items.map(item => ({
                ...item,
                special: cartSpecials[item.productId] || null,
                price: item[pricingType.standard] || 0,
                specialPrice: item[pricingType.special] || 0
            }));

            const cartDataForGTM = {
                ...cartData,
                items: cartItemsWithSpecials
            };

            hasTrackedViewCart.current = true;
            
            gtmTrackEvent('view_cart', cartDataForGTM);
        }

        previousCartIsOpen.current = cartIsOpen;
    }, [cartIsOpen, cartSidebar, isFetchedCartData, cartData, analytics, cartSpecials, pricingType]);

    if (!groupedItems) {
        return null;
    }
    
    return (
        <>
            {Object.keys(groupedItems).map((productId) => (
                <CartProduct
                    key={productId}
                    items={groupedItems[productId]}
                    pricingType={pricingType}
                    surfside={surfside}
                    analytics={analytics}
                    specialData={cartSpecials[productId] || null}
                />
            ))}
        </>
    );
};

export default CartProducts;
